import { useState, useEffect } from 'react';
import { Button, Input, Card, StatsCard, useToast } from '../components/common';
import api from '../services/api';

interface ProfileData {
  id: number;
  email: string;
  username: string;
  is_superuser: boolean;
  created_at: string;
  total_requests: number;
  success_rate: number;
  total_tokens: number;
  total_traffic_bytes: number;
}

export default function ProfilePage() {
  const { addToast } = useToast();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [changing, setChanging] = useState(false);

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get<ProfileData>('/api/v1/profile');
      setProfile(response.data);
    } catch (error) {
      addToast('error', '無法載入個人資料');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      addToast('error', '新密碼與確認密碼不符');
      return;
    }
    if (passwords.new.length < 8) {
      addToast('error', '新密碼至少需要 8 個字元');
      return;
    }
    try {
      setChanging(true);
      await api.put('/api/v1/profile/password', {
        current_password: passwords.current,
        new_password: passwords.new
      });
      addToast('success', '密碼已更新');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error: any) {
      addToast('error', error.response?.data?.error?.message || '密碼更新失敗');
    } finally {
      setChanging(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTokens = (tokens: number) => {
    if (tokens >= 1000000) return (tokens / 1000000).toFixed(2) + 'M';
    if (tokens >= 1000) return (tokens / 1000).toFixed(1) + 'K';
    return tokens.toString();
  };

  // 計算 Token 成本：每百萬 Token $0.05
  const calculateTokenCost = (tokens: number) => {
    const cost = (tokens / 1000000) * 0.05;
    if (cost < 0.01) return '< $0.01';
    return `$${cost.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!profile) {
    return <div className="text-center py-8 text-gray-500 dark:text-gray-400">無法載入個人資料</div>;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">個人資料</h1>

      {/* Stats - 2x2 grid */}
      <div className="grid grid-cols-2 gap-4">
        <StatsCard title="總請求數" value={profile.total_requests} icon="📊" />
        <StatsCard title="成功率" value={`${profile.success_rate.toFixed(1)}%`} icon="✅" />
        <StatsCard 
          title="總 Token" 
          value={formatTokens(profile.total_tokens || 0)} 
          icon="🔢" 
          subtitle={`預估成本：${calculateTokenCost(profile.total_tokens || 0)}`}
        />
        <StatsCard title="總流量" value={formatBytes(profile.total_traffic_bytes || 0)} icon="📡" />
      </div>

      {/* Profile Info */}
      <Card>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">基本資訊</h2>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">電子郵件</label>
              <p className="text-gray-900 dark:text-white">{profile.email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">用戶名稱</label>
              <p className="text-gray-900 dark:text-white">{profile.username}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">角色</label>
              <p className="text-gray-900 dark:text-white">{profile.is_superuser ? '管理員' : '一般用戶'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">建立時間</label>
              <p className="text-gray-900 dark:text-white">{new Date(profile.created_at).toLocaleDateString('zh-TW')}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Change Password */}
      <Card>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">變更密碼</h2>
        </div>
        <form onSubmit={handleChangePassword} className="p-4 space-y-4">
          <Input
            label="目前密碼"
            type="password"
            value={passwords.current}
            onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
            required
          />
          <Input
            label="新密碼"
            type="password"
            value={passwords.new}
            onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
            required
          />
          <Input
            label="確認新密碼"
            type="password"
            value={passwords.confirm}
            onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
            required
          />
          <Button type="submit" disabled={changing}>
            {changing ? '更新中...' : '更新密碼'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
